import { mergeDeepRight } from 'ramda';
import { readLocallyStored } from '../local-storage';
import getConfig from '../config';
import { getPrefixedLogger, prefixes } from '../rwc-logger';
import { FEATURE_FLAGS, FFS_FEATURE_FLAGS } from '../constants/feature-flags';
import type { AnyObject, RWCConfig } from '../types';
import FeatureFlagService from './service/feature-flag-service';
import type {
    FeatureFlagKey,
    RWCConfigFeatureEntity,
    FeatureFlagIdentifyInfo,
    FeatureFlag,
    OverrideFeatureFlag,
    FeatureFlagTransform,
    FeatureFlagSourceName,
    RWCExternalConfigFeatureEntity,
} from '../feature-flag/types';
import { FeatureFlagEvent } from '../feature-flag/types';
import { EventEmitter } from '../event-emitter';
import type { FeatureFlagController } from './interface/feature-flag-controller';
import { IS_FEATURE_FLAGS_ENABLED, IS_LAUNCH_DARKLY_ENABLED } from '../desktop-tools/feature-flags';
import {
    FeatureFlagsGetter,
    getDesktopLaunchDarklyAPI,
    getSetFeatureFlagGetter,
} from '../desktop-tools/desktop-api';

const INITIALIZATION_TIMEOUT = 5000;
const logger = getPrefixedLogger(prefixes.VC_UTILS, 'feature-flags-client');

type FlagsToDesktop = Record<string, FeatureFlag>;

export type FlagV2Calculation<FeatureEntity extends RWCConfigFeatureEntity> = Readonly<{
    result: FeatureEntity;
    sources: {
        configFlag: FeatureEntity | undefined;
        remoteFlag: Partial<FeatureEntity> | null;
        localStorageFlag: boolean | Partial<FeatureEntity> | undefined;
        sessionStorageFlag: Partial<FeatureEntity> | undefined;
    };
}>;

export const LOCAL_STORAGE_FEATURE_FLAG_PREFIX = 'f_';

/**
 * To allow overriding feature flags using localStorage, use:
 * Run in DevTools -> localStorage.setItem('f_breakoutRooms', true)
 * A JSON value deep-merges into the resolved flag (overrides FFS/config props):
 * Run in DevTools -> localStorage.setItem('f_breakoutRooms', JSON.stringify({ enabled: true, maxRooms: 50 }))
 */
const LOCAL_STORAGE_FEATURE_FLAGS = Object.fromEntries(
    Object.entries(FEATURE_FLAGS).map(([k, v]) => [v, LOCAL_STORAGE_FEATURE_FLAG_PREFIX + k])
);

/**
 * Used to get the final feature flag value
 * Combines app.config, localstorage and FFS
 * Call getFlag(key) as before to get the value
 */
class FeatureFlagModule extends EventEmitter {
    private _featureFlagService!: FeatureFlagService;
    private _externalFlags: Record<string, RWCConfigFeatureEntity> = {};

    constructor() {
        super();
    }
    get featureFlagService() {
        if (!this._featureFlagService) {
            this._featureFlagService = new FeatureFlagService();
        }
        return this._featureFlagService;
    }
    /**
     * whether all the remote sources(ffs) disabled
     */
    get isRemoteFeatureFlagEnabled() {
        return this.featureFlagService.isEnabled;
    }

    /**
     * get all feature flag from remote(FFS),not the local feature flag from app.config.js
     * we need to pass the feature flags to desktop sdk
     */
    private _getAllRemoteFlags(): FlagsToDesktop {
        const allFlags = this.featureFlagService.getAllFlags();
        const allRemoteFlags: FlagsToDesktop = {};
        for (const key in allFlags) {
            const item = allFlags[key];
            allRemoteFlags[item.originKey.toUpperCase()] = item.featureFlag;
        }
        return allRemoteFlags;
    }
    /**
     * pass the feature flags to desktop sdk (RCV-43679)
     */
    private _passFlagsToDesktop(flags: FlagsToDesktop) {
        if (!IS_LAUNCH_DARKLY_ENABLED) return;
        try {
            const setDesktopFlags = getDesktopLaunchDarklyAPI() as (
                flags: Record<string, unknown>
            ) => void;
            if (setDesktopFlags) {
                logger.info('pass feature flags to desktop', { ...flags });
                setDesktopFlags({ ...flags });
            }
        } catch (e) {
            const error = e as any; // TODO: RCV-99509 fix ts after update ts
            logger.error('Failed to pass feature flags to desktop', error);
        }
    }
    /**
     * pass the feature getter to desktop sdk (RCV-59464)
     */
    private _passFlagsGetterToDesktop() {
        if (!IS_FEATURE_FLAGS_ENABLED) return;

        try {
            const setFeatureFlagGetter = getSetFeatureFlagGetter() as (
                getFlag: FeatureFlagsGetter
            ) => void;
            logger.info('pass feature flags getter to desktop');
            setFeatureFlagGetter(key => this.getFlagV2(key as FeatureFlagKey));
        } catch (error) {
            logger.error('Failed to pass feature flags getter to desktop', error);
        }
    }

    /**
     * get feature flag from app.config.json
     * @param flagKey
     */
    public getConfigFlag<FeatureEntity extends RWCConfigFeatureEntity>(
        flagKey: FeatureFlagKey
    ): FeatureEntity {
        const sourceConfig = getConfig('FEATURES')?.[flagKey] as FeatureEntity;
        return sourceConfig;
    }

    private _onInitiaized = () => {
        this._passFlagsToDesktop(this._getAllRemoteFlags());
        this.emit(FeatureFlagEvent.INITIALIZED);
    };

    private _onChanged = () => {
        this._passFlagsToDesktop(this._getAllRemoteFlags());
        this.emit(FeatureFlagEvent.CHANGED);
    };

    private _initListeners() {
        this.featureFlagService.on?.(FeatureFlagEvent.INITIALIZED, this._onInitiaized);
        this.featureFlagService.on?.(FeatureFlagEvent.CHANGED, this._onChanged);
    }

    private _castRemoteFlagToFeatureEntity<FeatureEntity extends RWCConfigFeatureEntity>(
        remoteFlag: FeatureFlag
    ): Partial<FeatureEntity> | null {
        if (remoteFlag == null) return remoteFlag;

        return (
            typeof remoteFlag === 'object'
                ? { enabled: false, ...remoteFlag }
                : { enabled: remoteFlag === true }
        ) as Partial<FeatureEntity>;
    }

    public addSource(
        controller: FeatureFlagController,
        priority: number,
        transform: FeatureFlagTransform
    ): void {
        this.featureFlagService.addSource(controller, priority, transform);
    }

    public isSourceSupported(sourceName: FeatureFlagSourceName) {
        return this.featureFlagService.isSourceSupported(sourceName);
    }

    public initialize() {
        this._initListeners();
        this._passFlagsGetterToDesktop();
    }

    public freeze() {
        this.featureFlagService.freeze();
    }

    public unfreeze() {
        this.featureFlagService.unfreeze();
    }

    /**
     * await the feature flag module initialization
     * @param {number} timeout in ms to wait initialization
     * pass flags getter to desktop when initialized
     * three situalition
     * 1.remote feature flag(FFS) disabled
     * 2.remote feature flag(FFS) initialized
     * 3.timeout (5000 ms)
     * @returns {Promise<boolean>}
     */
    public awaitInitializing(timeout = INITIALIZATION_TIMEOUT) {
        return new Promise<boolean>(resolve => {
            const resolveInitializedStatus = (isInitializedOnTime: boolean) => {
                this._passFlagsGetterToDesktop();
                resolve(isInitializedOnTime);
            };
            if (!this.featureFlagService.isEnabled || this.featureFlagService.isInitialized) {
                resolveInitializedStatus(true);
                return;
            }

            const timer = setTimeout(() => {
                resolveInitializedStatus(false);
            }, timeout);

            this.featureFlagService.on?.(FeatureFlagEvent.INITIALIZED, () => {
                clearTimeout(timer);
                resolveInitializedStatus(true);
            });
        });
    }

    /**
     * @deprecated - will be removed in RCV-107453, use getFlagV2 instead.
     * Calculates a feature flag object based on values from app.config, localStorage, sessionStorage,
     * and ffs flags.
     *
     * Note on flag storage priority:
     * - first, get config and ffs flags, 'enabled' prop is calculated using boolean OR
     * - then, localStorage feature key may be used to override 'enabled' prop
     * - finally, sessionStorage may override any previously set value
     *
     * @param {string} flagKey same as app.config key
     * @param {string=} accountId [deprecated] current user's accountId
     * @param {Object.<string, boolean>=} overrideFeatureFlags [deprecated] flags which can be overrided
     * @returns {{enabled: boolean, [key: string]: any}} feature flag object with 'enabled' prop and optional configuration props
     */
    public getFlag<FeatureEntity extends RWCConfigFeatureEntity>(
        flagKey: FeatureFlagKey,
        /** @deprecated */
        accountId?: string | null,
        /** @deprecated */
        overrideFeatureFlags?: OverrideFeatureFlag
    ): FeatureEntity {
        const sessionStorageFlag = readLocallyStored<RWCConfig | null>('appConfig', null, false)
            ?.FEATURES?.[flagKey] as FeatureEntity | undefined;

        const localStorageFlagKey = LOCAL_STORAGE_FEATURE_FLAGS[flagKey];
        const localStorageFlag = readLocallyStored(localStorageFlagKey, undefined, true);

        const configFlag = (this._externalFlags[flagKey] ??
            this.getConfigFlag(flagKey)) as FeatureEntity;

        const overrideFlagExist =
            overrideFeatureFlags &&
            Boolean(Object.keys(overrideFeatureFlags).length) &&
            Object.prototype.hasOwnProperty.call(overrideFeatureFlags, flagKey);
        const featureFlagEnabled = overrideFlagExist
            ? overrideFeatureFlags?.[flagKey]
            : this.featureFlagService.getFeatureFlagEnabled(flagKey);

        const isEnabled =
            localStorageFlag !== undefined
                ? localStorageFlag
                : (accountId && (configFlag?.idAccounts ?? []).includes(accountId)) ||
                  featureFlagEnabled ||
                  Boolean(configFlag?.enabled);

        return {
            ...configFlag,
            enabled: isEnabled,
            ...sessionStorageFlag,
        };
    }

    /**
     * Computes a feature flag object by deeply merging app config, remote flag, localStorage and sessionStorage configs.
     * Unlike the previous version of getFlag, this method can handle JSON remote flags.
     * TODO: RCV-107453 replace getFlag with getFlagV2
     */
    public getFlagV2<FeatureEntity extends RWCConfigFeatureEntity>(
        flagKey: FeatureFlagKey
    ): Readonly<FeatureEntity> {
        return this.getFlagV2Calculation<FeatureEntity>(flagKey).result;
    }

    public getFlagV2Calculation<FeatureEntity extends RWCConfigFeatureEntity>(
        flagKey: FeatureFlagKey
    ): FlagV2Calculation<FeatureEntity> {
        const sessionStorageFlag = readLocallyStored<RWCConfig | null>('appConfig', null, false)
            ?.FEATURES?.[flagKey] as FeatureEntity | undefined;
        const lsFlagKey = LOCAL_STORAGE_FEATURE_FLAGS[flagKey];
        const localStorageFlag = readLocallyStored(lsFlagKey, undefined, true);

        const configFlag = (this._externalFlags[flagKey] ??
            this.getConfigFlag(flagKey)) as FeatureEntity;

        const rawRemoteFlag = this.getRemoteFeatureFlag(flagKey);
        const remoteFlag = this._castRemoteFlagToFeatureEntity<FeatureEntity>(rawRemoteFlag);

        let res: AnyObject = configFlag || {};

        if (remoteFlag) res = mergeDeepRight(configFlag, remoteFlag);
        if (localStorageFlag !== undefined) {
            if (localStorageFlag !== null && typeof localStorageFlag === 'object') {
                res = mergeDeepRight(res, localStorageFlag);
            } else {
                res.enabled = localStorageFlag;
            }
        }
        if (typeof sessionStorageFlag === 'object') res = mergeDeepRight(res, sessionStorageFlag);

        res.enabled = res.enabled ?? false;
        const sources = { configFlag, remoteFlag, localStorageFlag, sessionStorageFlag };
        logger.debug(`getFlagV2 ${flagKey}:`, res, 'sources:', sources);

        return { result: res as FeatureEntity, sources };
    }

    /**
     * set external flag, type can help you choose the origin of the flag (FFS, app.config)
     * ignore the app.config feature flag
     * @param key new feature flag key
     * @param value new feature flag value
     */
    public setFlag(key: string, value: RWCExternalConfigFeatureEntity) {
        this._externalFlags[key] = value;
        FEATURE_FLAGS[value.key] = value.key;
        LOCAL_STORAGE_FEATURE_FLAGS[value.key] = value.key;
        switch (value.type) {
            case 'FFS':
                FFS_FEATURE_FLAGS[value.key] = value.key;
                break;
        }
    }

    /**
     * set array of external flags
     * ignore the app.config feature flag
     * @param values all external flag values
     */
    public setFlags(values: RWCExternalConfigFeatureEntity[]) {
        for (const value of values) {
            this.setFlag(value.key, value);
        }
    }

    /**
     * just get the pure flag value from remote(FFS)
     * ignore the app.config feature flag
     * @param flagKey
     */
    public getRemoteFeatureFlag<T extends FeatureFlag>(flagKey: FeatureFlagKey): T | null {
        const remoteFeatureFlag = this.featureFlagService.getFeatureFlag(flagKey) as T;
        return remoteFeatureFlag;
    }

    public getFlagVersion() {
        return this.featureFlagService.getFlagVersion();
    }
    /**
     * identify the user and refresh the flags
     * @param identifyInfo
     * @param callback
     */
    public identifyUser(identifyInfo: FeatureFlagIdentifyInfo) {
        this.featureFlagService.identify?.(identifyInfo);
    }

    public getAllFlags() {
        return this.featureFlagService.getAllFlags();
    }
}

export { FeatureFlagModule };
